import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { buildPathWithPreviewToken } from '@/lib/previewEnvironment';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    
    // Log error to console for development
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Here you would integrate with a monitoring service like Sentry
    // Example: Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
    this.logErrorToService(error, errorInfo);
  }

  private logErrorToService(error: Error, errorInfo: ErrorInfo) {
    // Prepare error payload for monitoring service
    const errorPayload = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    };
    
    // Log to console in development
    if (import.meta.env.DEV) {
      console.group('🚨 Error Report');
      console.error('Error:', error);
      console.info('Component Stack:', errorInfo.componentStack);
      console.info('Payload:', errorPayload);
      console.groupEnd();
    }
    
    // TODO: Send to Sentry or other monitoring service
    // fetch('/api/log-error', { method: 'POST', body: JSON.stringify(errorPayload) });
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = buildPathWithPreviewToken('/');
  };

  private handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <Card className="max-w-lg w-full shadow-lg border-destructive/20">
            <CardHeader className="text-center pb-4">
              <div className="mx-auto w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
                <AlertTriangle className="h-8 w-8 text-destructive" />
              </div>
              <CardTitle className="text-xl text-foreground">
                Ops! Algo deu errado
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                Ocorreu um erro inesperado. Nossa equipe foi notificada e está trabalhando para resolver.
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-4">
              {import.meta.env.DEV && this.state.error && (
                <div className="bg-muted/50 rounded-lg p-4 overflow-auto max-h-40">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-2">
                    <Bug className="h-4 w-4" />
                    Detalhes do erro (apenas em desenvolvimento)
                  </div>
                  <pre className="text-xs text-destructive whitespace-pre-wrap break-words">
                    {this.state.error.message}
                  </pre>
                </div>
              )}
            </CardContent>
            
            <CardFooter className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button 
                variant="outline" 
                className="w-full sm:w-auto" 
                onClick={this.handleRetry}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Tentar novamente
              </Button>
              <Button 
                variant="outline" 
                className="w-full sm:w-auto" 
                onClick={this.handleGoHome}
              >
                <Home className="h-4 w-4 mr-2" />
                Ir para início
              </Button>
              <Button 
                className="w-full sm:w-auto" 
                onClick={this.handleReload}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Recarregar página
              </Button>
            </CardFooter>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
